import { parse, type AstNode, type CommandNode, type TextSegment } from "@pix/script";
import {
  CHARA_POSITIONS,
  type BgCell,
  type BgmCell,
  type CharaCell,
  type CharaPosition,
  type MessageCell,
  type SceneGrid,
  type SceneRow,
  type SeCell,
  type StateMarker,
  type VariableOp,
} from "./gridTypes";

/**
 * .pix ソース → SceneGrid。
 *
 * 単純化:
 *  - if/for/macro はブロックを展開せず、内側だけ平坦に走査(=条件無視で全パス見える)
 *  - 1 メッセージ = 1 行
 *  - 制御(jump/choice/end)はそれ自体を 1 行として表示
 *  - @set などの変数操作は次のメッセージ行の variableOps に集約
 */
export function parseToGrid(source: string, filename: string): SceneGrid {
  let nodes: AstNode[] = [];
  let error: string | null = null;
  try {
    const file = parse(source, filename);
    nodes = file.nodes;
  } catch (e) {
    error = (e as Error).message;
  }
  const builder = new GridBuilder();
  builder.walk(nodes);
  // 後処理: 各行の sourceRange を「block 単位(前の行の末尾+1 〜 この行のブロック末尾)」で確定
  assignBlockRanges(builder.rows, source);
  return {
    filename,
    rows: builder.rows,
    error,
  };
}

function assignBlockRanges(rows: SceneRow[], source: string): void {
  const totalLines = source.split(/\r?\n/).length;
  let prevEnd = 0;
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i]!;
    const start = prevEnd + 1;
    let end = row.sourceLine;
    if (row.message !== null) {
      end = findBlockEnd(source, row.sourceLine, "speech");
    } else if (row.choices) {
      end = findBlockEnd(source, row.sourceLine, "choice");
    }
    // 末尾の行は EOF まで含める(尾の @cmd を取り込まないよう、次の行があれば抑制)
    if (i === rows.length - 1) {
      end = Math.max(end, Math.min(totalLines, end));
    }
    row.sourceRange = { start: Math.max(1, start), end: Math.max(start, end) };
    prevEnd = row.sourceRange.end;
  }
}

function findBlockEnd(source: string, startLine: number, kind: "speech" | "choice"): number {
  const lines = source.split(/\r?\n/);
  let endIdx = startLine - 1;
  if (kind === "choice") {
    while (
      endIdx + 1 < lines.length &&
      /^\s*-\s+\[/.test(lines[endIdx + 1] ?? "")
    ) {
      endIdx++;
    }
  } else {
    while (endIdx + 1 < lines.length) {
      const next = lines[endIdx + 1]!;
      const trimmed = next.trim();
      if (trimmed === "") break;
      if (trimmed.startsWith("@")) break;
      if (trimmed.startsWith("## ")) break;
      if (trimmed.startsWith("> ")) break;
      if (/^\s*-\s+\[/.test(next)) break;
      if (trimmed.startsWith(";;") || trimmed.startsWith("//")) break;
      if (/^[^@>:;/\-\s][^:]*:\s+/.test(trimmed)) break;
      endIdx++;
    }
  }
  return endIdx + 1;
}

class GridBuilder {
  rows: SceneRow[] = [];
  private state = freshState();
  private pendingMarkers: StateMarker[] = [];
  private pendingVariableOps: VariableOp[] = [];
  private pendingChanged = freshChanged();
  private rowStartLine = 1;
  private currentSpeaker: string | null = null;

  walk(nodes: AstNode[]): void {
    this.walkAll(nodes);
    // 末尾にぶら下がっているマーカーがあれば「ぶら下がり行」として emit
    if (this.pendingMarkers.length > 0) {
      this.emitMarkerRow();
    }
  }

  private walkAll(nodes: AstNode[]): void {
    for (const node of nodes) this.walkOne(node);
  }

  private walkOne(node: AstNode): void {
    switch (node.type) {
      case "Label":
        if (this.pendingMarkers.length > 0) this.emitMarkerRow();
        this.pendingMarkers.push({ kind: "label", name: node.name });
        this.rowStartLine = node.line;
        return;
      case "Speech":
        this.emitMessageRow({
          speaker: node.speaker,
          plainText: textToPlain(node.segments),
        }, node.line);
        return;
      case "Narration":
        this.emitMessageRow({ speaker: null, plainText: textToPlain(node.segments) }, node.line);
        return;
      case "Choice": {
        if (this.pendingMarkers.length > 0) this.emitMarkerRow();
        const row = this.buildBaseRow(node.line);
        row.markers.push({ kind: "choice" });
        row.choices = node.options.map((o) => ({
          text: textToPlain(o.text),
          target: o.targetFile ? `${o.targetFile}#${o.targetLabel}` : `#${o.targetLabel}`,
          conditionRaw: o.condition ? "(条件付き)" : undefined,
        }));
        this.rows.push(row);
        return;
      }
      case "If":
        // 簡略: すべての分岐を順に展開して見せる
        for (const br of node.branches) {
          this.walkAll(br.body);
        }
        if (node.elseBody) this.walkAll(node.elseBody);
        return;
      case "For":
        this.walkAll(node.body);
        return;
      case "Macro":
        if (this.pendingMarkers.length > 0) this.emitMarkerRow();
        this.pendingMarkers.push({ kind: "macro", name: node.name });
        this.walkAll(node.body);
        return;
      case "Command":
        this.handleCommand(node);
        return;
    }
  }

  private handleCommand(node: CommandNode): void {
    const name = node.name;
    const sub = node.subcommand;
    const args = node.args;

    if (name === "bg") {
      if (sub === "hide" || args["hide"]) {
        if (this.state.bg !== null) this.pendingChanged.bg = true;
        this.state.bg = null;
      } else {
        const bgName = String(args["name"] ?? "");
        if (bgName) {
          if (!this.state.bg || this.state.bg.name !== bgName) {
            this.pendingChanged.bg = true;
          }
          this.state.bg = {
            name: bgName,
            fade: args["fade"] !== undefined ? Number(args["fade"]) : undefined,
          };
        }
      }
    } else if (name === "bgm") {
      if (sub === "play") {
        const file = String(args["file"] ?? args["play"] ?? "");
        if (file && file !== "__volume__") {
          if (!this.state.bgm || this.state.bgm.file !== file) {
            this.pendingChanged.bgm = true;
          }
          this.state.bgm = {
            file,
            vol: args["vol"] !== undefined ? Number(args["vol"]) : undefined,
          };
        }
      } else if (sub === "stop") {
        if (this.state.bgm !== null) this.pendingChanged.bgm = true;
        this.state.bgm = null;
      }
    } else if (name === "se") {
      if (sub === "play") {
        const loopRaw = args["loop"];
        const loop =
          loopRaw === undefined
            ? undefined
            : loopRaw === true || loopRaw === "true" || Number(loopRaw) !== 0;
        this.state.se = {
          kind: "play",
          file: String(args["file"] ?? ""),
          ...(args["slot"] !== undefined ? { slot: Number(args["slot"]) } : {}),
          ...(args["vol"] !== undefined ? { vol: Number(args["vol"]) } : {}),
          ...(loop !== undefined ? { loop } : {}),
          sourceLine: node.line,
        };
      } else if (sub === "stop") {
        this.state.se = {
          kind: "stop",
          ...(args["slot"] !== undefined ? { slot: Number(args["slot"]) } : {}),
          sourceLine: node.line,
        };
      }
    } else if (name === "chara") {
      this.handleChara(sub, args);
    } else if (name === "face") {
      // フェイスウィンドウは独立 — グリッドには反映しない(Phase β で別列か?)
    } else if (name === "jump") {
      if (this.pendingMarkers.length > 0) this.emitMarkerRow();
      const target = parseTargetRaw(args);
      const row = this.buildBaseRow(node.line);
      row.markers.push({ kind: "jump", target });
      this.rows.push(row);
    } else if (name === "call") {
      if (this.pendingMarkers.length > 0) this.emitMarkerRow();
      const target = parseTargetRaw(args);
      const row = this.buildBaseRow(node.line);
      row.markers.push({ kind: "call", target });
      this.rows.push(row);
    } else if (name === "return") {
      if (this.pendingMarkers.length > 0) this.emitMarkerRow();
      const row = this.buildBaseRow(node.line);
      row.markers.push({ kind: "return" });
      this.rows.push(row);
    } else if (name === "end") {
      if (this.pendingMarkers.length > 0) this.emitMarkerRow();
      const row = this.buildBaseRow(node.line);
      row.markers.push({ kind: "end" });
      this.rows.push(row);
    } else if (name === "set" || name === "unset") {
      // @set local.affection += 1 など
      this.pendingVariableOps.push({
        kind: name,
        raw: String(args["_raw"] ?? "").trim(),
        line: node.line,
      });
    } else if (name === "wait") {
      if (this.pendingMarkers.length > 0) this.emitMarkerRow();
      const row = this.buildBaseRow(node.line);
      const ms = args["ms"] !== undefined ? Number(args["ms"]) : undefined;
      row.markers.push({ kind: "wait", ...(ms !== undefined ? { ms } : {}) });
      this.rows.push(row);
    } else if (name === "transition") {
      if (this.pendingMarkers.length > 0) this.emitMarkerRow();
      const row = this.buildBaseRow(node.line);
      const effect = String(args["effect"] ?? args["type"] ?? sub ?? "fade");
      const ms = args["ms"] !== undefined ? Number(args["ms"]) : undefined;
      row.markers.push({
        kind: "transition",
        effect,
        ...(ms !== undefined ? { ms } : {}),
      });
      this.rows.push(row);
    } else if (name === "effect") {
      if (this.pendingMarkers.length > 0) this.emitMarkerRow();
      const row = this.buildBaseRow(node.line);
      row.markers.push({
        kind: "effect",
        name: String(sub ?? args["name"] ?? "?"),
        args,
      });
      this.rows.push(row);
    } else if (name === "screen") {
      if (this.pendingMarkers.length > 0) this.emitMarkerRow();
      const row = this.buildBaseRow(node.line);
      row.markers.push({
        kind: "screen",
        effect: String(sub ?? args["effect"] ?? "?"),
        args,
      });
      this.rows.push(row);
    }
  }

  private handleChara(
    sub: string | undefined,
    args: Record<string, unknown>,
  ): void {
    const id = String(args["id"] ?? "");
    if (!id) return;
    const action = sub ?? "show";
    if (action === "hide" || action === "hide_all") {
      if (action === "hide_all") {
        for (const p of CHARA_POSITIONS) {
          if (this.state.chara[p]) this.pendingChanged.chara[p] = true;
          this.state.chara[p] = null;
        }
      } else {
        for (const p of CHARA_POSITIONS) {
          if (this.state.chara[p]?.id === id) {
            this.pendingChanged.chara[p] = true;
            this.state.chara[p] = null;
          }
        }
      }
      return;
    }
    if (action === "face" || action === "move" || action === "show") {
      const expression = String(args["face"] ?? args["expression"] ?? "normal");
      const pos = (args["pos"] as string) ?? this.findCharaPos(id) ?? "center";
      if (CHARA_POSITIONS.includes(pos as CharaPosition)) {
        const cp = pos as CharaPosition;
        const cur = this.state.chara[cp];
        if (!cur || cur.id !== id || cur.expression !== expression) {
          this.pendingChanged.chara[cp] = true;
        }
        // 同じ id が別ポジションにいたら消す(face/move 時)
        if (action !== "show") {
          for (const p of CHARA_POSITIONS) {
            if (p !== cp && this.state.chara[p]?.id === id) {
              this.pendingChanged.chara[p] = true;
              this.state.chara[p] = null;
            }
          }
        }
        this.state.chara[cp] = { id, expression };
      }
    }
  }

  private findCharaPos(id: string): CharaPosition | null {
    for (const p of CHARA_POSITIONS) {
      if (this.state.chara[p]?.id === id) return p;
    }
    return null;
  }

  private emitMessageRow(message: MessageCell, line: number): void {
    if (message.speaker !== null) {
      this.currentSpeaker = message.speaker;
    }
    const row = this.buildBaseRow(line);
    row.message = message;
    this.rows.push(row);
  }

  private emitMarkerRow(): void {
    // メッセージのないマーカー専用行(ラベル定義など)
    const row = this.buildBaseRow(this.rowStartLine);
    this.rows.push(row);
  }

  private buildBaseRow(line: number): SceneRow {
    const id = `r${this.rows.length + 1}`;
    const markers = this.pendingMarkers;
    const variableOps = this.pendingVariableOps;
    const changed = this.pendingChanged;
    this.pendingMarkers = [];
    this.pendingVariableOps = [];
    this.pendingChanged = freshChanged();

    // SE はワンショットなので、この行で表示後にリセット
    const se = this.state.se;
    this.state.se = null;

    return {
      id,
      index: this.rows.length + 1,
      sourceLine: line,
      bgm: this.state.bgm,
      se,
      bg: this.state.bg,
      chara: { ...this.state.chara },
      changed,
      message: null,
      markers,
      variableOps,
      sourceRange: { start: line, end: line },
    };
  }
}

// =================================================================
// helpers
// =================================================================

interface State {
  bgm: BgmCell | null;
  se: SeCell | null;
  bg: BgCell | null;
  chara: Record<CharaPosition, CharaCell | null>;
}

function freshState(): State {
  return {
    bgm: null,
    se: null,
    bg: null,
    chara: {
      "left-edge": null,
      left: null,
      center: null,
      right: null,
      "right-edge": null,
    },
  };
}

function freshChanged(): SceneRow["changed"] {
  return {
    bgm: false,
    bg: false,
    chara: {
      "left-edge": false,
      left: false,
      center: false,
      right: false,
      "right-edge": false,
    },
  };
}

function textToPlain(segs: TextSegment[]): string {
  let out = "";
  for (const s of segs) {
    if (s.kind === "text") out += s.value;
    else if (s.kind === "tag" && !s.closing) {
      if (s.name === "br") out += "\n";
      else if (s.name === "ruby") {
        const base = s.attrs["base"] ?? s.attrs["_arg0"] ?? "";
        const ruby = s.attrs["text"] ?? s.attrs["_arg1"] ?? "";
        out += ruby ? `${base}(${ruby})` : base;
      } else if (s.name === "var") {
        const path = s.attrs["name"] ?? s.attrs["value"] ?? s.attrs["_arg0"] ?? "";
        out += `{${path}}`;
      }
    }
  }
  return out;
}

function parseTargetRaw(args: Record<string, unknown>): string {
  const raw = String(args["_raw"] ?? "").trim();
  return raw || "?";
}
